import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import ToggleDropdown from '../reused-components/SidebarTabs';
import AddProducts from '../reused-components/AddProducts';
import Profile from '../reused-components/Profile';
import smlogo from '../../img/sm-logo.svg';
import profileImage from '../../img/user-profile.png';
import TelemetryFormBlock from '../reused-components/FormBlock';
import { fetchSiteInfo } from "../../services/apiService";
import {
    fetchAllSitePlugins,
    invalidateSitePlugins,
} from "../../services/pluginPlanService";
import { resolvePlanTierFromPlugins } from "../../utils/pluginPlan";
import ThemesPluginsTable from '../reused-components/ThemesPluginsTable';
import ProductEventsLogTable from '../tables/ProductEventsLogTable';
import TelemetryAccordion from '../reused-components/Accordian';
import DarkModeSwitch from "../reused-components/ToggleDarkMode";
import PlanBadge from '../reused-components/PlanBadge';
import { useProductSlugs } from '../../hooks/useProductSlugs';

export default function SiteDetails() {
    document.title = "SiteDetails | Telemetry";

    const { siteId } = useParams(); // Extract siteId from the URL
    const navigate = useNavigate();
    const {storeId, productId} = useParams();
    const { freeSlug, proSlug } = useProductSlugs();
    const [sitePlanTier, setSitePlanTier] = useState(null);

    const [SiteData, setSiteInfo] = useState({
        url: '',
        title: '',
        pluginVersion: '',
        sdkVersion: '',
        phpVersion: '',
        wordpressVersion: '',
        locale: '',
        location: '',
        owner: '',
        user_id: '',
        created_at: ''
    });

    const handleUserDetailClick = (userId) => {
        navigate(`/stores/${storeId}/plugins/${productId}/users/${userId}`);
    };

    useEffect(() => {
        if (!siteId) {
            console.warn('siteId is undefined!');
            return;
        }

        const fetchData = async () => {
            try {
                const data = await fetchSiteInfo(siteId);

                // Map meta_data if it exists, otherwise use default values
                const metaData = (data.meta_data || []).reduce((acc, item) => {
                    acc[item.meta_key] = item.meta_value;
                    return acc;
                }, {});

                setSiteInfo({
                    url: data.site_url || '',
                    title: data.site_title || '',
                    created_at: data.created_at || '',
                    pluginVersion: data.plugin_version || '',
                    sdkVersion: data.sdk_version || '',
                    phpVersion: metaData.php_version || '',
                    wordpressVersion: metaData.wp_version || '',
                    locale: metaData.wp_locale || '',
                    location: metaData.wp_location || '',
                    owner: data.owner || '',
                    user_id: data.user_id || ''
                });
            } catch (error) {
                console.error('Error fetching site details:', error);
            }
        };

        fetchData();
    }, [siteId]);

    useEffect(() => {
        if (!siteId || !freeSlug) {
            setSitePlanTier(null);
            return undefined;
        }

        let cancelled = false;
        invalidateSitePlugins(siteId);

        fetchAllSitePlugins(siteId, { forceRefresh: true }).then((plugins) => {
            if (!cancelled) {
                setSitePlanTier(resolvePlanTierFromPlugins(plugins, freeSlug, proSlug));
            }
        });

        return () => {
            cancelled = true;
        };
    }, [siteId, freeSlug, proSlug]);

    return (
        <div className="telemetry-page-wrapper">
            <header className="telemetry-header">
                <div className="telemetry-logo-section">
                    <a href="#"><img src={smlogo} alt="Telemetry" /></a>
                </div>
                <div className="telemetry-top">
                    <h1>Sites</h1>
                    <div className="telemetry-top-right">
					<DarkModeSwitch />
                        <AddProducts />
                        <Profile image={profileImage} />
                    </div>
                </div>
            </header>
            <main className="telemetry-main">
                <aside className="telemetry-sidebar">
                    <ToggleDropdown tabactive={1} tabactive2={1} />
                </aside>
                <section className="telemetry-content">
                    <div className="telemetry-settings-tab">
                        <div className="telemetry-setting-tab-content">
                            <div className="telemetry-setting-box">
                                <h2 className="telemetry-setting-heading">
                                    Site Information
                                    <PlanBadge tier={sitePlanTier} />
                                </h2>
                                <div className="telemetry-box-ov">
                                    <div className="telemetry-setting-block-2">
                                        <TelemetryFormBlock
                                            label="URL"
                                            inputType="url"
                                            value={SiteData.url}
                                            name="url"
                                            id="url"
                                        />
                                        <TelemetryFormBlock
                                            label="Title"
                                            inputType="text"
                                            value={SiteData.title}
                                            name="title"
                                            id="title"
                                        />
                                        <TelemetryFormBlock
                                            label="Installed at"
                                            inputType="text"
                                            value={SiteData.created_at}
                                            name="installedat"
                                            id="installedat"
                                            width="telemetry-form-block-1-4"
                                            disabled={true}
                                        />
                                        <TelemetryFormBlock
                                            label="Country"
                                            inputType="text"
                                            value={SiteData.location}
                                            name="location"
                                            id="location"
                                            width="telemetry-form-block-1-4"
                                            disabled={true}
                                        />
                                        <TelemetryFormBlock
                                            label="Language"
                                            inputType="text"
                                            value={SiteData.locale}
                                            name="locale"
                                            id="locale"
                                            width="telemetry-form-block-1-4"
                                            disabled={true}
                                        />
                                        <TelemetryFormBlock
                                            label="Plugin Version"
                                            inputType="text"
                                            value={SiteData.pluginVersion}
                                            name="pluginVersion"
                                            id="pluginVersion"
                                            width="telemetry-form-block-1-4"
                                            disabled={true}
                                        />
                                        <TelemetryFormBlock
                                            label="WordPress Version"
                                            inputType="text"
                                            value={SiteData.wordpressVersion}
                                            name="wordpressVersion"
                                            id="wordpressVersion"
                                            width="telemetry-form-block-1-4"
                                            disabled={true}
                                        />
                                        <TelemetryFormBlock
                                            label="SDK Version"
                                            inputType="text"
                                            value={SiteData.sdkVersion}
                                            name="sdkVersion"
                                            id="sdkVersion"
                                            width="telemetry-form-block-1-4"
                                            disabled={true}
                                        />
                                        <TelemetryFormBlock
                                            label="PHP Version"
                                            inputType="text"
                                            value={SiteData.phpVersion}
                                            name="phpVersion"
                                            id="phpVersion"
                                            width="telemetry-form-block-1-4"
                                            disabled={true}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="telemetry-setting-box owner">
                                <h3>Admin & Owner</h3>
                                <div className="telemetry-box-ov">
                                    <span onClick={() => handleUserDetailClick(SiteData.user_id)} className="link-icon">{SiteData.owner}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <TelemetryAccordion title="Themes and Plugins">
                        <ThemesPluginsTable siteId={siteId} />
                    </TelemetryAccordion>
                    <TelemetryAccordion title="Events">
                        <ProductEventsLogTable paginationPerPage={10} type={'all'} siteId={siteId} />
                    </TelemetryAccordion>
                </section>
            </main>
        </div>
    );
}
